/**
 * Fallback Content Manager
 *
 * Handles offline mode and fallback content for digital signage players.
 * Based on industry best practices from Fugo, PiSignage, MagicINFO, and Kitcast.
 *
 * Priority Order:
 * 1. Primary Content (scheduled playlist)
 * 2. Cached Primary Content (if network fails during playback)
 * 3. Fallback Playlist (designated emergency content)
 * 4. Bundled Default Content (ships with player)
 * 5. Emergency Message (text-based, always available)
 */

import { config } from '../config';
import { getConfig, setConfig, getCachedAssetIds } from '../utils/storage';
import type { DeviceManifest, ManifestPlaylist } from '../types';

// Storage keys
const FALLBACK_STORAGE_KEYS = {
  LAST_GOOD_MANIFEST: 'govista_last_good_manifest',
  FALLBACK_ASSETS: 'govista_fallback_assets',
  NETWORK_STATUS: 'govista_network_status',
};

// Default emergency content (pure CSS/HTML, no external resources)
const EMERGENCY_CONTENT = {
  type: 'emergency' as const,
  backgroundColor: '#1a1a2e',
  textColor: '#ffffff',
  message: 'Content is loading...',
  subMessage: 'Please wait while we restore your content.',
  logoText: 'GoVista',
};

export type NetworkStatus = 'online' | 'offline' | 'unstable';

export type FallbackReason =
  | 'network_offline'
  | 'manifest_failed'
  | 'asset_failed'
  | 'playlist_empty'
  | 'schedule_gap'
  | 'first_boot';

export interface FallbackState {
  isUsingFallback: boolean;
  reason?: FallbackReason;
  networkStatus: NetworkStatus;
  lastOnlineTime: number;
  cachedAssetsCount: number;
  hasFallbackPlaylist: boolean;
}

export type FallbackCallback = (state: FallbackState) => void;

class FallbackContentManager {
  private networkStatus: NetworkStatus = 'online';
  private lastOnlineTime: number = Date.now();
  private isUsingFallback = false;
  private fallbackReason?: FallbackReason;
  private stateCallback?: FallbackCallback;
  private networkCheckInterval?: ReturnType<typeof setInterval>;
  private lastGoodManifest?: DeviceManifest;
  private onlineHandler: (() => void) | null = null;
  private offlineHandler: (() => void) | null = null;

  /**
   * Initialize the fallback manager
   */
  async initialize(callback?: FallbackCallback): Promise<void> {
    this.stateCallback = callback;

    // Set up network status detection
    this.setupNetworkDetection();

    // Load last known good manifest
    await this.loadLastGoodManifest();

    // Initial state notification
    this.notifyStateChange();

    console.log('[FallbackManager] Initialized', {
      networkStatus: this.networkStatus,
      hasLastGoodManifest: !!this.lastGoodManifest,
    });
  }

  /**
   * Set up network status detection using navigator.onLine and events
   */
  private setupNetworkDetection(): void {
    // Clean up any existing listeners and interval to prevent leaks
    this.cleanupNetworkDetection();

    // Initial status
    this.networkStatus = navigator.onLine ? 'online' : 'offline';

    // Create bound handlers so we can remove them later
    this.onlineHandler = () => {
      console.log('[FallbackManager] Network online');
      this.networkStatus = 'online';
      this.lastOnlineTime = Date.now();
      this.notifyStateChange();
    };

    this.offlineHandler = () => {
      console.log('[FallbackManager] Network offline');
      this.networkStatus = 'offline';
      this.notifyStateChange();
    };

    // Listen for online/offline events
    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    // Periodic connectivity check (more reliable than navigator.onLine alone)
    this.networkCheckInterval = setInterval(() => {
      this.checkNetworkConnectivity();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Clean up network detection resources
   */
  private cleanupNetworkDetection(): void {
    if (this.networkCheckInterval) {
      clearInterval(this.networkCheckInterval);
      this.networkCheckInterval = undefined;
    }
    if (this.onlineHandler) {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
    if (this.offlineHandler) {
      window.removeEventListener('offline', this.offlineHandler);
      this.offlineHandler = null;
    }
  }

  /**
   * Check actual network connectivity by attempting a fetch.
   *
   * Uses the API server's /health endpoint with an absolute URL. The
   * previous build used a relative `/health` path, which on every
   * native-shelled player (webOS, Tizen, Electron) resolves against the
   * `file://` origin of the installed bundle — `file:///health` →
   * ERR_FILE_NOT_FOUND on every probe. The network was reported as
   * "unstable" even when it was perfectly fine. Use config.baseUrl so
   * we hit the same server we're already talking to.
   */
  private async checkNetworkConnectivity(): Promise<void> {
    try {
      // Try to fetch a small resource with a timeout
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      await fetch(`${config.baseUrl}/health`, {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-store',
      });

      clearTimeout(timeout);

      if (this.networkStatus !== 'online') {
        console.log('[FallbackManager] Network restored');
        this.networkStatus = 'online';
        this.lastOnlineTime = Date.now();
        this.notifyStateChange();
      }
    } catch {
      // If we thought we were online but fetch failed
      if (this.networkStatus === 'online') {
        // Give it a few tries before marking offline
        this.networkStatus = 'unstable';
        console.log('[FallbackManager] Network unstable');
        this.notifyStateChange();
      }
    }
  }

  /**
   * Load the last known good manifest from storage
   */
  private async loadLastGoodManifest(): Promise<void> {
    try {
      const saved = await getConfig<DeviceManifest>(FALLBACK_STORAGE_KEYS.LAST_GOOD_MANIFEST);
      if (saved) {
        this.lastGoodManifest = saved;
      }
    } catch (error) {
      console.warn('[FallbackManager] Failed to load last good manifest:', error);
    }
  }

  /**
   * Save a manifest as the last known good state
   */
  async saveGoodManifest(manifest: DeviceManifest): Promise<void> {
    try {
      this.lastGoodManifest = manifest;
      await setConfig(FALLBACK_STORAGE_KEYS.LAST_GOOD_MANIFEST, manifest);
      console.log('[FallbackManager] Saved good manifest v' + manifest.version);
    } catch (error) {
      console.warn('[FallbackManager] Failed to save good manifest:', error);
    }
  }

  /**
   * Get the fallback playlist from manifest
   */
  getFallbackPlaylist(manifest: DeviceManifest): ManifestPlaylist | undefined {
    const fallbackId = manifest.schedule?.fallbackPlaylistId;
    if (!fallbackId) return undefined;

    return manifest.playlists.find(p => p.id === fallbackId);
  }

  /**
   * Check if we have cached assets for a playlist
   */
  async hasPlaylistCached(manifest: DeviceManifest, playlistId: string): Promise<boolean> {
    const playlist = manifest.playlists.find(p => p.id === playlistId);
    if (!playlist) return false;

    const cachedIds = new Set(await getCachedAssetIds());
    const requiredAssetIds = playlist.items.map(item => item.assetId).filter((id): id is string => !!id);

    // Check if all required assets are cached
    return requiredAssetIds.every(id => cachedIds.has(id));
  }

  /**
   * Determine the best available content source
   */
  async getBestContentSource(
    manifest: DeviceManifest | null,
    activePlaylistId: string
  ): Promise<{
    playlist: ManifestPlaylist | null;
    reason: FallbackReason | null;
    source: 'primary' | 'cached' | 'fallback' | 'emergency';
  }> {
    // 1. Try primary playlist with fresh manifest
    if (manifest && this.networkStatus === 'online') {
      const primaryPlaylist = manifest.playlists.find(p => p.id === activePlaylistId);
      if (primaryPlaylist && primaryPlaylist.items.length > 0) {
        this.isUsingFallback = false;
        this.fallbackReason = undefined;
        return { playlist: primaryPlaylist, reason: null, source: 'primary' };
      }
    }

    // 2. Try cached primary playlist from last good manifest
    if (this.lastGoodManifest && this.networkStatus !== 'online') {
      const cached = await this.hasPlaylistCached(this.lastGoodManifest, activePlaylistId);
      if (cached) {
        const cachedPlaylist = this.lastGoodManifest.playlists.find(p => p.id === activePlaylistId);
        if (cachedPlaylist) {
          this.isUsingFallback = true;
          this.fallbackReason = 'network_offline';
          return { playlist: cachedPlaylist, reason: 'network_offline', source: 'cached' };
        }
      }
    }

    // 3. Try fallback playlist
    const effectiveManifest = manifest || this.lastGoodManifest;
    if (effectiveManifest) {
      const fallbackPlaylist = this.getFallbackPlaylist(effectiveManifest);
      if (fallbackPlaylist) {
        const hasFallbackCached = await this.hasPlaylistCached(effectiveManifest, fallbackPlaylist.id);
        if (hasFallbackCached || this.networkStatus === 'online') {
          this.isUsingFallback = true;
          this.fallbackReason = manifest ? 'playlist_empty' : 'network_offline';
          return { playlist: fallbackPlaylist, reason: this.fallbackReason, source: 'fallback' };
        }
      }
    }

    // 4. Emergency content (no playlist, just show message)
    this.isUsingFallback = true;
    this.fallbackReason = manifest ? 'manifest_failed' : 'first_boot';
    return { playlist: null, reason: this.fallbackReason, source: 'emergency' };
  }

  /**
   * Get emergency content configuration
   */
  getEmergencyContent(): typeof EMERGENCY_CONTENT {
    return EMERGENCY_CONTENT;
  }

  /**
   * Render emergency content to a container
   */
  renderEmergencyContent(container: HTMLElement): void {
    const content = this.getEmergencyContent();

    container.innerHTML = `
      <div style="
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background-color: ${content.backgroundColor};
        color: ${content.textColor};
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        text-align: center;
        padding: 2rem;
      ">
        <div style="
          font-size: 3rem;
          font-weight: bold;
          margin-bottom: 1rem;
          background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        ">
          ${content.logoText}
        </div>
        <div style="
          width: 60px;
          height: 60px;
          border: 4px solid rgba(255,255,255,0.2);
          border-top-color: #00d4ff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 2rem;
        "></div>
        <div style="font-size: 1.5rem; margin-bottom: 0.5rem;">
          ${content.message}
        </div>
        <div style="font-size: 1rem; opacity: 0.7;">
          ${content.subMessage}
        </div>
        <style>
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        </style>
      </div>
    `;
  }

  /**
   * Get current state
   */
  async getState(): Promise<FallbackState> {
    const cachedIds = await getCachedAssetIds();
    const effectiveManifest = this.lastGoodManifest;
    const hasFallbackPlaylist = effectiveManifest
      ? !!this.getFallbackPlaylist(effectiveManifest)
      : false;

    return {
      isUsingFallback: this.isUsingFallback,
      reason: this.fallbackReason,
      networkStatus: this.networkStatus,
      lastOnlineTime: this.lastOnlineTime,
      cachedAssetsCount: cachedIds.length,
      hasFallbackPlaylist,
    };
  }

  /**
   * Notify callback of state change
   */
  private async notifyStateChange(): Promise<void> {
    if (this.stateCallback) {
      const state = await this.getState();
      this.stateCallback(state);
    }
  }

  /**
   * Mark that we're entering fallback mode
   */
  enterFallbackMode(reason: FallbackReason): void {
    if (!this.isUsingFallback || this.fallbackReason !== reason) {
      console.log('[FallbackManager] Entering fallback mode:', reason);
      this.isUsingFallback = true;
      this.fallbackReason = reason;
      this.notifyStateChange();
    }
  }

  /**
   * Mark that we're exiting fallback mode
   */
  exitFallbackMode(): void {
    if (this.isUsingFallback) {
      console.log('[FallbackManager] Exiting fallback mode');
      this.isUsingFallback = false;
      this.fallbackReason = undefined;
      this.notifyStateChange();
    }
  }

  /**
   * Get network status
   */
  getNetworkStatus(): NetworkStatus {
    return this.networkStatus;
  }

  /**
   * Check if currently in fallback mode
   */
  isInFallbackMode(): boolean {
    return this.isUsingFallback;
  }

  /**
   * Get the last good manifest
   */
  getLastGoodManifest(): DeviceManifest | undefined {
    return this.lastGoodManifest;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.cleanupNetworkDetection();
  }
}

export const fallbackManager = new FallbackContentManager();
